import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
} from "@heroui/react";
import { FaCheck } from "react-icons/fa6";
import { usePlans } from "@/hooks/usePlans";
import { Plan } from "@/api/plans";
import { PLAN_FEATURES, FEATURE_NAMES } from "@/app/store/plans/constants";

export function PlansPage() {
  const {
    currentPlan,
    availablePlans,
    isLoading,
    error,
    isPlanUpgrade,
    isPlanDowngrade,
  } = usePlans();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "RUB",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const isCurrentPlan = (plan: Plan) => {
    return currentPlan?.id === plan.id;
  };

  const getPlanFeatures = (plan: Plan) => {
    const features = [];

    if (plan.office_count > 0) {
      features.push(`${plan.office_count} офисов`);
    } else if (plan.office_count === -1) {
      features.push("Неограниченное количество офисов");
    }

    if (plan.location_count > 0) {
      features.push(`${plan.location_count} локаций`);
    } else if (plan.location_count === -1) {
      features.push("Неограниченное количество локаций");
    }

    return features;
  };

  const getPlanType = (plan: Plan) => {
    if (isCurrentPlan(plan)) return "current";
    if (isPlanUpgrade(plan)) return "upgrade";
    if (isPlanDowngrade(plan)) return "downgrade";
    return "same";
  };

  const getPlanPermissions = (plan: Plan) => {
    const permissions: string[] = [];

    Object.entries(PLAN_FEATURES).forEach(([, bit]) => {
      if ((plan.mask & bit) === bit) {
        permissions.push(FEATURE_NAMES[bit]);
      }
    });

    return permissions;
  };

  if (isLoading) {
    return (
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" color="primary" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <p className="text-danger mb-4">{error}</p>
              <Button color="primary" onPress={() => window.location.reload()}>
                Попробовать снова
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row flex-wrap gap-4 w-full">
      <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
        <div className="flex flex-row justify-between w-full items-center mb-4">
          <div className="flex flex-row gap-4 items-center">
            <span className="font-medium">Тарифные планы</span>
            {currentPlan && (
              <Chip color="primary" variant="flat">
                Текущий план: {currentPlan.display_name}
              </Chip>
            )}
          </div>
        </div>

        {availablePlans && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {availablePlans
              .filter((plan) => plan.public)
              .map((plan) => {
                const planType = getPlanType(plan);
                const planPermissions = getPlanPermissions(plan);

                return (
                  <Card
                    key={plan.id}
                    className={`relative flex flex-col shadow-none border-2 border-foreground-200 dark:border-foreground-100 ${isCurrentPlan(plan) ? "ring-2 ring-primary" : ""}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-semibold">
                            {plan.display_name}
                          </h3>
                          {isCurrentPlan(plan) && (
                            <Chip
                              color="primary"
                              size="sm"
                              variant="flat"
                              className="mt-1"
                            >
                              Текущий план
                            </Chip>
                          )}
                          {planType === "upgrade" && (
                            <Chip
                              color="success"
                              size="sm"
                              variant="flat"
                              className="mt-1"
                            >
                              Улучшение
                            </Chip>
                          )}
                          {planType === "downgrade" && (
                            <Chip
                              color="warning"
                              size="sm"
                              variant="flat"
                              className="mt-1"
                            >
                              Понижение
                            </Chip>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardBody className="pt-0 flex-1 flex flex-col">
                      <div className="mb-4">
                        <div className="text-3xl font-bold text-primary mb-1">
                          {formatPrice(plan.price)}
                        </div>
                        <div className="text-sm text-foreground-500">
                          в месяц
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {getPlanFeatures(plan).map((feature, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <FaCheck className="text-success text-sm" />
                            <span className="text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {planPermissions.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium mb-2 text-foreground-600">
                            Доступные функции:
                          </h4>
                          <div className="flex flex-wrap gap-1 max-h-20 overflow-hidden">
                            {planPermissions.map((permission, index) => (
                              <Chip
                                key={index}
                                variant="bordered"
                                size="sm"
                                className="text-xs"
                              >
                                {permission}
                              </Chip>
                            ))}
                          </div>
                        </div>
                      )}

                      <Button
                        color={isCurrentPlan(plan) ? "default" : "primary"}
                        variant={isCurrentPlan(plan) ? "bordered" : "solid"}
                        className="w-full mt-auto"
                        disabled={isCurrentPlan(plan)}
                      >
                        {isCurrentPlan(plan) ? "Текущий план" : "Выбрать план"}
                      </Button>
                    </CardBody>
                  </Card>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
